// Libraries
import { useRef, useCallback } from 'react';
import { point, bounds } from 'leaflet';
import FadeLoader from "react-spinners/FadeLoader";

import { selectVariable } from './Global';
import { ChangeYear, PollingLoc } from './Types';

// Function from https://www.typescriptlang.org/play?ssl=26&ssc=3&pln=26&pc=62#code/JYWwDg9gTgLgBAJQKYEMDGMA0cDecCuAzksgGbZFIDCKANrQEboDWcAvnKVBCHAORRUGPgG4AUKEixccJAA8wSDABUAnovaduvPjEIATPmLEB6AFRmxcM3ACCaNEjB64KOAHNgANyQA7OGh0jCzYKL76cGDcXsD6SISucL5IAO4BQUxorCnAMAAWiYQwKAy0SHCCpEiCvo6YVjYpecBoBTn0cLnEtKSutCkoqgmBHfnltCgw8fA+UITAEP4QvWOR0bFIESPBWQB0DXAAqsSk+LSc0HBxdMC+7nA5+XD5wFARYCiwqgE8kMm+LnykwIxDgRTo5TQtAghHwgkI+2sJjEp1qMAW-koAGViqVqBkWAAeWxQdwJeRTcIJfC+Zi+CApXwAbQAuthkDA4b4AHwACm2mWYAC44LzduLPmSRSSyQBKOAAXm5iCQnKgvnlOCsP18RXS9EFZEVIJISFI-IJWVl4jg+p2zDIuzQcJq8AVdsF4m1aEWevBeJoBpYxsogftvLFEtJhGl0flStw2ttgjV-gFLEdzqgrsju0lhGt2rY2FZhe1Ka5YNxZTDnrEbGMqIwGLgADEIBBeZrtU30YsCLrq-ig1leSBCO4RUUoLd3NgfTSYCLfPgQAxqrKpzAZ3dE7bbSYTFcIFX8KRenlqkgkxVVZWAAYrtfVToJAAkOAXAI4YQi4-uwAxnAH7-mw942uwxi2j6g5ViUNaWqw7rYkOtYsLyNL+ghI7MGWB5HighDELALakJcqx5B2rAVuqzzqEgQravIigqPRhJjhOW47nOPyLsuq7rlA8bKtOs58lhw72nhcCHnA94SWhWT3uk-j0mk66RIRxARDAJ5MFsvyLH4gJ5MClBwWUATQrC8JQbeqZwIS+jeNyLwJIBzyXhcJ4+uARkAoSJjOV43L1mIQA
export function useStableCallback<Args extends unknown[], Return>(callback: (...args: Args) => Return) {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;
  
    const stableCallback = useCallback((...args: Args) => {
      return callbackRef.current(...args);
    }, []);
  
    return stableCallback;
}

// Return true if there's data for that county during the selected time period
export function returnCountyShouldInteract(changeYear: ChangeYear, properties: any) {
  return (selectVariable as { [key: string]: any }).changeYear.find((d: any) => d.changeYear === changeYear.changeYear)?.[properties.stabbr];
}

// Returns the bounds of the current map view
export function getMapBounds(mapRef: any) {
  const mapBounds = mapRef.current.getBounds();
  const mapNE = mapBounds?.getNorthEast();
  const mapSW = mapBounds?.getSouthWest();

  return bounds(point(mapSW!.lat, mapSW!.lng), point(mapNE!.lat, mapNE!.lng));
}

// Returns a list of geographies which are current in view
export function filterGeoByBounds(mapRef: any, data: any) {

  const mapBounds = getMapBounds(mapRef);                          

  const features: any[] = [];

  data.features.forEach((d: any) => {

      var p1 = point(d.properties.bounds.southWest.lat, d.properties.bounds.southWest.lng),
          p2 = point(d.properties.bounds.northEast.lat, d.properties.bounds.northEast.lng),
          tractBounds = bounds(p1, p2);

      if (mapBounds.intersects(tractBounds)) {
          features.push(d);
      }
  });

  return {type: 'FeatureCollection', features: features} as GeoJSON.FeatureCollection;
}

export function filterPointByBounds(mapRef: any, data: any) {

  const mapBounds = getMapBounds(mapRef);

  const points: PollingLoc[] = [];

  data.filter((d: any) => d.missingDataFlag === false)
    .forEach((d: any) => {
      const p = point(d.latlng.lat, d.latlng.lng);
      if (mapBounds.contains(p)) {
          d.pixelCoord = mapRef.current.latLngToLayerPoint(d.latlng);
          points.push(d);
      }
  });

  return points;
}

export  function partOfSelectedState(data: GeoJSON.FeatureCollection, stfp: string) {

  if (stfp === '') {
    return data.features;
  } else {
    return data.features.map((d: GeoJSON.Feature) => {
        if (d.properties!.stfp === stfp) {
            return { ...d, properties: { ...d.properties, selected: true } };
        } else {
            return { ...d, properties: { ...d.properties, selected: false } };
        }
    });
  }
}

export function partOfSelectedCounty(data: GeoJSON.FeatureCollection, cntyfp: string) {

  if (cntyfp === '') {
    return data.features;
  } else {
    return data.features.map((d: GeoJSON.Feature) => {
        if (d.properties!.cntyfp === cntyfp) {
            return { ...d, properties: { ...d.properties, selected: true } };
        } else {
            return { ...d, properties: { ...d.properties, selected: false } };
        }
    });
  }
}

export function Loader({loading}: {loading: boolean} ) {
  return (
      <div className="Loader">
          <FadeLoader
              loading={loading}
              aria-label="Loading Spinner"
              data-testid="loader"
          />
      </div>
  );
}
